/**
 * Shared locale-aware feedback copy — form errors, network errors, auth
 * errors, save/copy confirmations (copy-voice-overhaul-2026-08-17 T5).
 *
 * Before this module, "Something went wrong. Please try again." and the
 * "Email hello@lokerdollar.com directly." variant were hand-copied into
 * 11+ files, independently drifting. Every consumer imports from here
 * instead of writing its own string.
 *
 * Canon rule 3 (docs/plans/copy-voice-overhaul-2026-08-17.md): what happened
 * -> what state (data safe? nothing charged?) -> one next step. Never lead
 * with "Something went wrong" / "An unexpected error occurred".
 *
 * None of these strings carry a 2nd-person pronoun (no "kamu"/"Anda" in the
 * id copy) — they read the same on seeker (kamu-register) and employer
 * (Anda-register) surfaces, so a single set is correct everywhere without a
 * register parameter.
 */

export const SUPPORT_EMAIL = "hello@lokerdollar.com";

type Locale = "en" | "id";

/** Generic form-submit failure, no support contact needed (retry is the whole ask). */
export function formErrorGeneric(locale: Locale): string {
	return locale === "en"
		? "Couldn't save that — nothing was lost. Try again in a moment."
		: "Gagal disimpan — tidak ada yang hilang. Coba lagi sebentar lagi.";
}

/** Form-submit failure where a paid/committed action may be involved — names the support email as the escape hatch. */
export function formErrorContactSupport(locale: Locale): string {
	return locale === "en"
		? `Couldn't send that — nothing was charged. Try again, or email ${SUPPORT_EMAIL}.`
		: `Gagal terkirim — belum ada yang dibayar. Coba lagi, atau email ${SUPPORT_EMAIL}.`;
}

/**
 * The payment provider refused the buyer's own details for exceeding a field
 * limit (today: Mayar's 55-character `email` cap — see `MAYAR_FIELD_LIMITS`).
 *
 * Distinct from `formErrorContactSupport` because retrying is not the next
 * step: the same address fails identically on every attempt, so the copy names
 * the actual blocker and makes the manual path the ask. The seeker rail has
 * said this since 2026-08-17 (`pricing-buttons.tsx`); this is the same promise
 * for every other checkout that mints a Mayar link.
 */
export function checkoutFieldTooLong(
	locale: Locale,
	field: string,
	limit: number,
): string {
	if (field === "email") {
		return locale === "en"
			? `That email address is too long for our payment provider (max ${limit} characters) — nothing was charged. Email ${SUPPORT_EMAIL} and we'll set it up manually.`
			: `Alamat email terlalu panjang untuk penyedia pembayaran (maks ${limit} karakter) — belum ada yang dibayar. Email ${SUPPORT_EMAIL} dan kami siapkan manual.`;
	}
	return locale === "en"
		? `Our payment provider limits ${field} to ${limit} characters — nothing was charged. Email ${SUPPORT_EMAIL} and we'll set it up manually.`
		: `Penyedia pembayaran membatasi ${field} sampai ${limit} karakter — belum ada yang dibayar. Email ${SUPPORT_EMAIL} dan kami siapkan manual.`;
}

/** Network/connectivity failure — distinct from a validation or server error. */
export function networkErrorGeneric(locale: Locale): string {
	return locale === "en"
		? "Connection issue — nothing was sent. Check your connection and try again."
		: "Masalah koneksi — belum terkirim. Cek koneksimu dan coba lagi.";
}

/** Checkout handoff took too long: no charge happened, retry is safe. */
export function checkoutTimeout(locale: Locale): string {
	return locale === "en"
		? "The payment page took too long to open — nothing was charged. Try again."
		: "Halaman pembayaran terlalu lama terbuka — belum ada yang dibayar. Coba lagi.";
}

/** Sign-in failure, mirrors src/app/[locale]/(public)/login/page.tsx's existing pattern. */
export function authErrorGeneric(locale: Locale): string {
	return locale === "en"
		? "Couldn't sign in — enter your email to try again."
		: "Gagal masuk — masukkan email untuk coba lagi.";
}

/**
 * OAuth (LinkedIn/Google) sign-in failure on the callback leg. Distinct from
 * `authErrorGeneric`: the visitor did nothing wrong and has no link to retry,
 * so the copy names the provider path as the thing that failed and points at
 * the email fallback that is right there on the page.
 */
export function oauthSignInError(locale: Locale): string {
	return locale === "en"
		? "That sign-in didn't go through — nothing was saved. Try again, or use your email below."
		: "Masuk lewat akun itu gagal — belum ada yang tersimpan. Coba lagi, atau pakai email di bawah.";
}

/**
 * Sign-in recovery copy — the headline + supporting line for a visitor who
 * arrived on `/login` because a sign-in attempt failed, rather than because
 * they chose to sign in.
 *
 * Split into a title and a detail because the login page gives this state the
 * page heading rather than a banner: the visitor's question on arrival is
 * "what happened to my link?", and a red bar under an unchanged "Welcome back"
 * answers it last. Title names the problem, detail carries canon rule 3's
 * remaining two beats — what state things are in, then the one next step.
 *
 * Here rather than in `common.json` for the same reason as `oauthSignInError`:
 * that dictionary is imported whole by every zone that re-exports the apex
 * public layout, so a key costs its bytes in every zone bundle.
 */
export function magicLinkExpiredTitle(locale: Locale): string {
	return locale === "en"
		? "That sign-in link has expired"
		: "Link masuk ini sudah kedaluwarsa";
}

export function magicLinkExpiredDetail(locale: Locale): string {
	return locale === "en"
		? "Sign-in links last one hour and work once. Nothing changed on the account — send a fresh link to the same inbox."
		: "Link masuk berlaku satu jam dan sekali pakai. Belum ada yang berubah di akun — kirim link baru ke email yang sama.";
}

/**
 * The rarer magic-link failures (`failed_to_create_user`,
 * `new_user_signup_disabled`, `failed_to_create_session`): the link itself was
 * fine, verification failed after it. Same recovery, different first line —
 * telling someone their link expired when it did not is how a support thread
 * starts.
 */
export function magicLinkFailedTitle(locale: Locale): string {
	return locale === "en"
		? "That sign-in link didn't go through"
		: "Link masuk itu gagal diproses";
}

export function magicLinkFailedDetail(locale: Locale): string {
	return locale === "en"
		? "Nothing was saved to the account. Enter an email address to get a new link."
		: "Belum ada yang tersimpan di akun. Masukkan alamat email untuk dapat link baru.";
}

/** Title beside `oauthSignInError`, for the same heading treatment. */
export function oauthSignInErrorTitle(locale: Locale): string {
	return locale === "en"
		? "That sign-in didn't go through"
		: "Masuk lewat akun itu gagal";
}

/** Micro-confirmation after a successful save (not an error — no canon-rule-3 structure needed). */
export function savedConfirmation(locale: Locale): string {
	return locale === "en" ? "Saved" : "Tersimpan";
}

/** Micro-confirmation after a successful clipboard copy. */
export function copiedConfirmation(locale: Locale): string {
	return locale === "en" ? "Copied" : "Disalin";
}
